"use client";

import {
    BUDGET_EUR_ANCHOR,
    BudgetBounds,
    budgetBoundsForCurrency,
    scaleBudgetBounds,
} from "@/common/utilities/money/budgetBounds";
import { useEffect, useMemo, useState } from "react";

/**
 * Budget-slider bounds in `currency`'s minor units, scaled from the EUR anchor.
 *
 * The rate lookup is asynchronous but the slider needs a range on first paint,
 * so the unscaled anchor renders immediately and is replaced once the rate
 * resolves. Both are usable ranges: the fallback is off in magnitude until the
 * rate lands, never absent.
 */
export const useBudgetBounds = (currency: string): BudgetBounds => {
    const fallback = useMemo(
        () => scaleBudgetBounds(BUDGET_EUR_ANCHOR, 1, currency),
        [currency]
    );
    // The currency is kept with the bounds so a resolved range is only used
    // for the currency it was resolved for. A currency change re-runs the
    // effect, but its rate lands a round trip later, and until then state
    // still holds the previous currency's range -- a yen span on a euro
    // slider. Falling back for that window shows a range that at least belongs
    // to the currency on screen.
    const [resolved, setResolved] = useState<{
        currency: string;
        bounds: BudgetBounds;
    } | null>(null);

    useEffect(() => {
        let active = true;
        void budgetBoundsForCurrency(currency).then((bounds) => {
            if (active) setResolved({ currency, bounds });
        });
        return () => {
            active = false;
        };
    }, [currency]);

    return resolved?.currency === currency ? resolved.bounds : fallback;
};
